import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { fetchCampaignMemberById, updateCampaignMember } from 'api/campaigns';
import { uploadCampaignMemberMedia } from 'api/media';
import * as MemberFormInputs from 'assets/js/pages/campaign-settings/campaign-members/MemberFormInputs';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { UploadFile } from 'components/upload';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface EditMemberFormValues extends MemberFormInputs.MemberManualFormValues {
    description: string;
}
const CampaignMembersEditMemberDrawer = () => {
    const toast = useToast();
    const { memberId: possibleMemberId } = useParams<{ memberId: string }>();
    const memberId = parseInt(possibleMemberId);
    const queryClient = useQueryClient();
    const { goBack } = useDialogRoute();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;

    const { data: member } = useSuspenseQuery({
        queryKey: campaignQueryKeys.memberById(campaignId, memberId),
        queryFn: () => fetchCampaignMemberById(campaignId, memberId),
    });

    const methods = Form.useForm<EditMemberFormValues>({
        defaultValues: {
            displayName: member?.display_name ?? '',
            goal: member?.goal ?? '',
            description: member?.description ?? '',
            team: member?.team
                ? [
                      {
                          id: member.team.id.toString(),
                          label: member.team.name,
                      },
                  ]
                : [],
            picture: member?.picture ? [{ id: 'picture', url: member.picture }] : [],
        },
    });
    const {
        path,
        control,
        handleSubmit,
        formState: { dirtyFields },
    } = methods;

    const { isPending: isLoading, mutate: submit } = useMutation({
        mutationFn: async (values: EditMemberFormValues) => {
            const nextValues = await MemberFormInputs.transformFormValues({
                ...values,
                picture: methods.getValues('picture'),
            });

            const pictures = methods.getValues('picture');
            const picture = pictures[0] as UploadFile;

            if (picture && member?.picture && member.picture !== picture.getMetadata('url')) {
                await uploadCampaignMemberMedia(campaignId, memberId, picture.file, 'profile');
            }

            return updateCampaignMember(campaignId, memberId, {
                ...nextValues,
                picture: undefined,
                description: values.description,
            });
        },
        onSuccess: () => {
            toast.success(`Updated team member successfully!`);
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.members(campaignId) });

            if (dirtyFields.team) {
                queryClient.invalidateQueries({ queryKey: campaignQueryKeys.teams(campaignId) });
            }

            goBack();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Unknown error occurred');
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(
                    submit as SubmitHandler<MemberFormInputs.MemberManualFormValues>,
                )}
            >
                <Drawer.Header>
                    <Drawer.Title>Update team member</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body className={'overflow-x-hidden'}>
                    <div className={'flex flex-col gap-y-6'}>
                        <div className={'flex flex-wrap gap-4'}>
                            <MemberFormInputs.Picture />
                            <div className={'flex flex-grow flex-col gap-4'}>
                                <MemberFormInputs.DisplayName />
                                <MemberFormInputs.Goal />
                                <MemberFormInputs.Team />
                            </div>
                        </div>
                        <Form.Container>
                            <Form.Label name={path('description')}>Story</Form.Label>
                            <Wysiwyg.Root>
                                <UseFormWysiwyg
                                    name={'description'}
                                    control={control}
                                    inline={false}
                                    init={{
                                        statusbar: false,
                                        placeholder: 'Optional',
                                        plugins: Wysiwyg.Constants.MINI_EDITOR_PLUGINS,
                                        toolbar: Wysiwyg.Constants.MINI_EDITOR_TOOLBAR,
                                        content_style: Wysiwyg.Constants.APP_FONT,
                                        menubar: false,
                                    }}
                                />
                            </Wysiwyg.Root>
                        </Form.Container>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel>Cancel</Drawer.Cancel>
                        <Form.SubmitButton isSubmitting={isLoading}>Save</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { CampaignMembersEditMemberDrawer };
